import React, { useEffect, useMemo, useRef, useState } from "react";
import type { DataExportRegistryItem } from "../../types/dataExport";
import {
  buildExportTypeGroups,
  exportTypeLabel,
} from "../../constants/dataExports";
import "./ExportTypeMultiselect.scss";

interface ExportTypeMultiselectProps {
  registry: DataExportRegistryItem[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}

const MENU_STYLE: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 2px)",
  left: 0,
  zIndex: 20,
  minWidth: "100%",
  width: "max-content",
  maxWidth: 360,
  maxHeight: 360,
  overflowY: "auto",
  background: "#fff",
  border: "1px solid #ced4da",
  borderRadius: 4,
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
  padding: "4px 0",
};

const TRIGGER_STYLE: React.CSSProperties = {
  height: 36,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  textAlign: "left",
  cursor: "pointer",
  paddingRight: 8,
};

const CATEGORY_STYLE: React.CSSProperties = {
  padding: "6px 12px 2px",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#6c757d",
};

const OPTION_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  margin: 0,
  padding: "6px 12px",
  fontSize: 14,
  color: "#212529",
  cursor: "pointer",
  whiteSpace: "nowrap",
  fontWeight: 400,
};

const DIVIDER_STYLE: React.CSSProperties = {
  margin: "4px 0",
  border: 0,
  borderTop: "1px solid #e9ecef",
};

/**
 * The registry marks the all-account zip as category "composite".
 * Mutual exclusivity is based on that category, not display copy.
 */
export const isFullAccountExport = (item: DataExportRegistryItem): boolean =>
  item.category === "composite";

export const isExportTypeOptionDisabled = (
  item: DataExportRegistryItem,
  selectedIds: string[],
  allItems: DataExportRegistryItem[]
): boolean => {
  if (selectedIds.length === 0) return false;
  const selectedItems = selectedIds
    .map((id) => allItems.find((entry) => entry.id === id))
    .filter((entry): entry is DataExportRegistryItem => Boolean(entry));
  const fullAccountSelected = selectedItems.some(isFullAccountExport);
  if (fullAccountSelected && !isFullAccountExport(item)) return true;

  const individualIds = allItems
    .filter((entry) => !isFullAccountExport(entry))
    .map((entry) => entry.id);
  const allIndividualsSelected =
    individualIds.length > 0 &&
    individualIds.every((id) => selectedIds.includes(id));
  if (allIndividualsSelected && isFullAccountExport(item)) return true;
  return false;
};

export const formatExportTypeSummary = (
  selectedIds: string[],
  itemsById: Map<string, DataExportRegistryItem>
): string => {
  if (selectedIds.length === 0) return "Select an export…";
  const labels = selectedIds
    .map((id) => {
      const item = itemsById.get(id);
      return item ? exportTypeLabel(item) : undefined;
    })
    .filter((label): label is string => Boolean(label));
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return labels.join(", ");
  return `${labels[0]} +${labels.length - 1} more`;
};

export const nextExportTypeSelection = (
  currentIds: string[],
  toggled: DataExportRegistryItem,
  allItems: DataExportRegistryItem[]
): string[] => {
  if (isExportTypeOptionDisabled(toggled, currentIds, allItems)) {
    return currentIds;
  }
  const isSelected = currentIds.includes(toggled.id);
  if (isSelected) {
    return currentIds.filter((id) => id !== toggled.id);
  }
  if (isFullAccountExport(toggled)) {
    return [toggled.id];
  }
  return [...currentIds, toggled.id];
};

function ExportTypeMultiselect({
  registry,
  selectedIds,
  onChange,
  disabled = false,
}: ExportTypeMultiselectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const groups = useMemo(() => buildExportTypeGroups(registry), [registry]);
  const itemsById = useMemo(
    () => new Map(registry.map((item) => [item.id, item])),
    [registry]
  );
  const summary = formatExportTypeSummary(selectedIds, itemsById);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const toggleItem = (item: DataExportRegistryItem) => {
    onChange(nextExportTypeSelection(selectedIds, item, registry));
  };

  return (
    <div ref={containerRef} style={{ position: "relative", width: 260 }}>
      <button
        type="button"
        className="form-control form-control-sm"
        style={TRIGGER_STYLE}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Export type"
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: selectedIds.length ? "#212529" : "#6c757d",
          }}
        >
          {summary}
        </span>
        <i
          className="ExportTypeMultiselect__chevron fas fa-chevron-down"
          aria-hidden="true"
        />
      </button>
      {open && (
        <div role="listbox" aria-multiselectable="true" style={MENU_STYLE}>
          {groups.map((group, groupIndex) => (
            <div key={group.category}>
              {groupIndex > 0 && <hr style={DIVIDER_STYLE} />}
              <div data-testid="export-category" style={CATEGORY_STYLE}>
                {group.label}
              </div>
              {group.items.map((item) => {
                const checked = selectedIds.includes(item.id);
                const optionDisabled = isExportTypeOptionDisabled(
                  item,
                  selectedIds,
                  registry
                );
                return (
                  <label
                    key={item.id}
                    style={{
                      ...OPTION_STYLE,
                      color: optionDisabled ? "#adb5bd" : "#212529",
                      cursor: optionDisabled ? "not-allowed" : "pointer",
                    }}
                    onMouseEnter={(event) => {
                      if (optionDisabled) return;
                      event.currentTarget.style.background = "#f8f9fa";
                    }}
                    onMouseLeave={(event) => {
                      event.currentTarget.style.background = "transparent";
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={optionDisabled}
                      onChange={() => toggleItem(item)}
                    />
                    <span>{exportTypeLabel(item)}</span>
                  </label>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ExportTypeMultiselect;
